import request from "../../utils/request";
import { Result } from "../../models/general";

// .NET enums serialize as the member NAME (Newtonsoft StringEnumConverter) — values must match exactly.
export enum BusinessRoleType {
  Department = "Department",
  Project = "Project",
  Temporary = "Temporary",
}

export enum EquipmentPriority {
  Essential = "Essential",
  Important = "Important",
  Helpful = "Helpful",
  Optional = "Optional",
}

export interface NeededEquipmentItem {
  categoryId: number;
  categoryName?: string;
  priority: EquipmentPriority;
  quantity: number;
}

export interface ClarityField {
  field: string;
  percent: number;
  note?: string;
}

export interface ClarityAnalysis {
  overallPercent: number;
  perField: ClarityField[];
  recommendations: string[];
  isUnclear: boolean;
  aiAvailable: boolean;
}

export enum BusinessRoleStatus {
  Active = "Active",
  Inactive = "Inactive",
  Draft = "Draft",
}

export interface BusinessRoleDependencyView {
  role: string;
  description: string;
}

export interface BusinessRoleSuccessCriteriaView {
  description?: string;
  lastReviewDate?: string;
  nextReviewDate?: string;
  reviewedBy?: string;
}

export interface BusinessRoleView {
  id: number;
  name: string;
  type: BusinessRoleType;
  description?: string;
  category?: string;
  status: BusinessRoleStatus;
  numberOfSeats: number;
  assignedCount: number;
  coreResponsibilities: string[];
  decisionRights: string[];
  dependencies: BusinessRoleDependencyView[];
  successCriteria?: BusinessRoleSuccessCriteriaView;
  tags: string[];
  requiredSkills: string[];
  requiredSkillItems: RequiredSkillItemView[];
  neededEquipment?: NeededEquipmentItem[];
  relatedEquipment?: number[];
  departmentId?: number;
  clarityScore?: number;
  /** When clarityScore was last recalculated. Absent on records saved before it existed. */
  clarityScoreUpdatedOn?: string;
  clarity?: ClarityAnalysis;
  createdOn: string;
  updatedOn?: string;
}

export interface BusinessRoleListView {
  items: BusinessRoleView[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  availableTags: string[];
}

export interface BusinessRoleStatsView {
  total: number;
  active: number;
  inactive: number;
  draft: number;
  unassigned: number;
  dueToRevision: number;
  needsClarity: number;
  isCompanyScope: boolean;
  scopeDepartmentId?: number;
  scopeDepartmentName?: string;
}

export interface BusinessRoleRevisionView {
  id: number;
  // CO-953 (6): no changer user id is sent (a system key); the name is all the client gets.
  changedByName?: string;
  changedAt: string;
  fieldName: string;
  oldValue?: string;
  newValue?: string;
  comment?: string;
}

export interface BusinessRoleDependencyInput {
  role: string;
  description: string;
}

export interface BusinessRoleSuccessCriteriaInput {
  description?: string;
  lastReviewDate?: string;
  nextReviewDate?: string;
  reviewedBy?: string;
}

export interface CreateBusinessRoleInput {
  name: string;
  type: BusinessRoleType;
  description?: string;
  category?: string;
  status: BusinessRoleStatus;
  numberOfSeats: number;
  coreResponsibilities?: string[];
  decisionRights?: string[];
  dependencies?: BusinessRoleDependencyInput[];
  successCriteria?: BusinessRoleSuccessCriteriaInput;
  tags?: string[];
  requiredSkills?: string[];
  requiredSkillItems?: RequiredSkillItemInput[];
  neededEquipment?: NeededEquipmentItem[];
  relatedEquipment?: number[];
  departmentId?: number;
}

export interface UpdateBusinessRoleInput extends CreateBusinessRoleInput {
  id: number;
  revisionComment?: string;
}

export interface BusinessRoleListQuery {
  search?: string;
  status?: BusinessRoleStatus;
  type?: BusinessRoleType;
  category?: string;
  tag?: string;
  filterTab?: string;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

// Role Assignment types (Story 4.2)

export enum RoleAssignmentStatus {
  Active = "Active",
  Expired = "Expired",
  Revoked = "Revoked",
}

export interface RoleAssignmentView {
  id: number;
  businessRoleId: number;
  roleName: string;
  roleType: BusinessRoleType;
  // CO-953 (6): no employee user id is sent; the panels show the name.
  employeeName?: string;
  effectiveDate: string;
  expiryDate?: string;
  notes?: string;
  status: RoleAssignmentStatus;
  assignedByName?: string;
  assignedAt: string;
}

export interface RoleConflictView {
  type: string;
  severity: string;
  message: string;
  existingRoleId?: number;
  existingRoleName?: string;
  overlappingResponsibilities: string[];
}

export interface ConflictDetectionResult {
  hasConflicts: boolean;
  conflicts: RoleConflictView[];
}

export interface RoleAssignmentListView {
  items: RoleAssignmentView[];
  totalCount: number;
}

export interface AssignRoleInput {
  businessRoleId: number;
  employeeId: string;
  effectiveDate: string;
  expiryDate?: string;
  notes?: string;
  forceAssign: boolean;
}

export interface UnassignRoleInput {
  assignmentId: number;
  notes?: string;
}

export interface EmployeeSimpleView {
  id: string;
  fullName: string;
  department?: string;
}

// Bulk Assignment types (Story 4.3)

export interface BulkAssignRoleInput {
  businessRoleId: number;
  employeeIds: string[];
  effectiveDate: string;
  expiryDate?: string;
  notes?: string;
  forceAssign: boolean;
}

export interface BulkAssignmentEmployeeResult {
  // CO-953 (6): no employee user id is sent back; rows are listed in request order with the name.
  employeeName?: string;
  success: boolean;
  errorMessage?: string;
  conflicts: RoleConflictView[];
}

export interface BulkAssignmentResult {
  totalRequested: number;
  successCount: number;
  failedCount: number;
  results: BulkAssignmentEmployeeResult[];
}

export interface UnifiedRevisionView {
  id: number;
  eventType: string; // "definition_change" | "assignment" | "revocation"
  /** CO-953 (6): a person made the change (no user id is sent). Absent from a pre-v1.0.73 server. */
  hasChangedBy?: boolean;
  changedByName?: string;
  changedAt: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  comment?: string;
  employeeName?: string;
}

// Role-Skill Alignment types (Task 12)

export interface RoleSkillAlignmentView {
  businessRoleId: number;
  roleName: string;
  /** Legacy: held at ANY level. Coarser than alignmentPercentage, which is strength-graded. */
  matchedCount: number;
  missingCount: number;
  totalRequired: number;
  /** Strength-graded and importance-weighted — the authoritative number the Q3 score uses. */
  alignmentPercentage: number;
  matchedSkills: string[];
  missingSkills: string[];
  requirements?: RequirementStateView[];
  unresolved?: UnresolvedSkillView[];
}

export interface RoleSkillAlignmentResult {
  roles: RoleSkillAlignmentView[];
}

// API functions

export async function getBusinessRoles(query: BusinessRoleListQuery): Promise<Result<BusinessRoleListView>> {
  const params = new URLSearchParams();
  if (query.search) params.append("search", query.search);
  if (query.status !== undefined) params.append("status", String(query.status));
  if (query.type !== undefined) params.append("type", String(query.type));
  if (query.category) params.append("category", query.category);
  // Was missing: `tag` was added to BusinessRoleListQuery and TeamRoleManagement set it, but it was
  // never appended here, so the tag filter selection never left the browser and the server returned
  // the unfiltered list every time. Optional field, so nothing caught it at compile time.
  if (query.tag) params.append("tag", query.tag);
  if (query.filterTab) params.append("filterTab", query.filterTab);
  if (query.sortBy) params.append("sortBy", query.sortBy);
  if (query.sortDescending !== undefined) params.append("sortDescending", String(query.sortDescending));
  if (query.page !== undefined) params.append("page", String(query.page));
  if (query.pageSize !== undefined) params.append("pageSize", String(query.pageSize));

  const response = await request.get<Result<BusinessRoleListView>>(`BusinessRole/GetBusinessRoles?${params.toString()}`);
  return response.data;
}

export async function getBusinessRole(id: number): Promise<Result<BusinessRoleView>> {
  const response = await request.get<Result<BusinessRoleView>>(`BusinessRole/GetBusinessRole/${id}`);
  return response.data;
}

export async function getBusinessRoleStats(): Promise<Result<BusinessRoleStatsView>> {
  const response = await request.get<Result<BusinessRoleStatsView>>("BusinessRole/GetBusinessRoleStats");
  return response.data;
}

export async function createBusinessRole(input: CreateBusinessRoleInput): Promise<Result<BusinessRoleView>> {
  const response = await request.post<Result<BusinessRoleView>>("BusinessRole/CreateBusinessRole", input);
  return response.data;
}

export async function updateBusinessRole(input: UpdateBusinessRoleInput): Promise<Result<BusinessRoleView>> {
  const response = await request.put<Result<BusinessRoleView>>("BusinessRole/UpdateBusinessRole", input);
  return response.data;
}

export async function archiveBusinessRole(id: number): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>(`BusinessRole/ArchiveBusinessRole/${id}`);
  return response.data;
}

export async function getRevisionHistory(roleId: number): Promise<Result<BusinessRoleRevisionView[]>> {
  const response = await request.get<Result<BusinessRoleRevisionView[]>>(`BusinessRole/GetRevisionHistory/${roleId}`);
  return response.data;
}

// Role Assignment API functions (Story 4.2)

export async function detectConflicts(businessRoleId: number, employeeId: string): Promise<Result<ConflictDetectionResult>> {
  const response = await request.get<Result<ConflictDetectionResult>>(
    `BusinessRole/DetectConflicts?businessRoleId=${businessRoleId}&employeeId=${encodeURIComponent(employeeId)}`
  );
  return response.data;
}

export async function assignRole(input: AssignRoleInput): Promise<Result<RoleAssignmentView>> {
  const response = await request.post<Result<RoleAssignmentView>>("BusinessRole/AssignRole", input);
  return response.data;
}

export async function unassignRole(input: UnassignRoleInput): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>("BusinessRole/UnassignRole", input);
  return response.data;
}

export async function getRoleAssignments(roleId: number): Promise<Result<RoleAssignmentListView>> {
  const response = await request.get<Result<RoleAssignmentListView>>(`BusinessRole/GetRoleAssignments/${roleId}`);
  return response.data;
}

export async function getEmployeeRoleAssignments(employeeId: string): Promise<Result<RoleAssignmentListView>> {
  const response = await request.get<Result<RoleAssignmentListView>>(
    `BusinessRole/GetEmployeeRoleAssignments/${encodeURIComponent(employeeId)}`
  );
  return response.data;
}

export async function searchEmployeesForAssignment(search: string): Promise<Result<EmployeeSimpleView[]>> {
  const response = await request.get<Result<EmployeeSimpleView[]>>(
    `BusinessRole/SearchEmployeesForAssignment?search=${encodeURIComponent(search)}`
  );
  return response.data;
}

// Bulk Assignment & Revision History API functions (Story 4.3)

export async function bulkAssignRole(input: BulkAssignRoleInput): Promise<Result<BulkAssignmentResult>> {
  const response = await request.post<Result<BulkAssignmentResult>>("BusinessRole/BulkAssignRole", input);
  return response.data;
}

export async function getUnifiedRevisionHistory(roleId: number): Promise<Result<UnifiedRevisionView[]>> {
  const response = await request.get<Result<UnifiedRevisionView[]>>(`BusinessRole/GetUnifiedRevisionHistory/${roleId}`);
  return response.data;
}

// Role-Skill Alignment API function (Task 12)

export async function getRoleSkillAlignment(employeeId: string): Promise<Result<RoleSkillAlignmentResult>> {
  const response = await request.get<Result<RoleSkillAlignmentResult>>(
    `BusinessRole/GetRoleSkillAlignment/${encodeURIComponent(employeeId)}`
  );
  return response.data;
}

// AI assistance (Q1 Role-Clarity) — mirrors backend BusinessRoleAiController shapes.

export interface RoleSuggestionView {
  description?: string;
  coreResponsibilities: string[];
  decisionRights: string[];
  dependencies: BusinessRoleDependencyView[];
  requiredSkills: string[];
  category?: string;
  neededEquipment: NeededEquipmentItem[];
  relatedEquipment: number[];
  successCriteria?: string;
  tags: string[];
  aiAvailable: boolean;
}

export interface RoleSuggestInput {
  name: string;
  category?: string;
  description?: string;
}

export interface SuccessCriteriaSuggestInput {
  name: string;
  coreResponsibilities?: string[];
  decisionRights?: string[];
}

export interface ClarityAnalyzeInput {
  name: string;
  description?: string;
  category?: string;
  coreResponsibilities?: string[];
  decisionRights?: string[];
  dependencies?: BusinessRoleDependencyInput[];
  successCriteria?: BusinessRoleSuccessCriteriaInput;
  neededEquipment?: NeededEquipmentItem[];
  requiredSkills?: string[];
}

export async function suggestRole(input: RoleSuggestInput): Promise<Result<RoleSuggestionView>> {
  const response = await request.post<Result<RoleSuggestionView>>("BusinessRoleAi/SuggestRole", input);
  return response.data;
}

export async function suggestSuccessCriteria(input: SuccessCriteriaSuggestInput): Promise<Result<string>> {
  const response = await request.post<Result<string>>("BusinessRoleAi/SuggestSuccessCriteria", input);
  return response.data;
}

export async function analyzeClarity(input: ClarityAnalyzeInput): Promise<Result<ClarityAnalysis>> {
  const response = await request.post<Result<ClarityAnalysis>>("BusinessRoleAi/AnalyzeClarity", input);
  return response.data;
}

// ----------Changes for Q3 clarity coverage -------------

// .NET enums serialize as the member NAME (Newtonsoft StringEnumConverter) — values must match exactly.
export enum SkillHoldLevel {
  NotHeld = "NotHeld",
  Awareness = "Awareness",
  Working = "Working",
  Strong = "Strong",
}

export enum SkillRequirementSource {
  Role = "Role",
  Goal = "Goal",
  Both = "Both",
}

export interface RequirementStateView {
  skillId: number;
  skillName: string;
  importance: EquipmentPriority;
  source: SkillRequirementSource;
  sourceDetail?: string;
  level: SkillHoldLevel;
  fill: number;
  weight: number;
  contribution: number;
  isLegacy: boolean;
}

export interface UnresolvedSkillView {
  rawName: string;
  businessRoleId: number;
  roleName?: string;
}

export interface StrengthAlignmentRowView {
  employeeId: string;
  employeeName?: string;
  department?: string;
  roleNames?: string;
  coveragePercent: number;
  utilizationPercent?: number;
  requirements: RequirementStateView[];
  unusedStrengths: string[];
  unresolved: UnresolvedSkillView[];
}

export interface SkillRoleGapsView {
  rows: StrengthAlignmentRowView[];
  underCoveredCount: number;
  totalWithRequirements: number;
  /** Q12 period window ("yyyy-MM") the server APPLIED, or null/absent for current requirements. */
  period?: string | null;
}

export interface RoleFitCandidateView {
  employeeId: string;
  employeeName?: string;
  department?: string;
  coveragePercent: number;
  strongCount: number;
  developingCount: number;
  missingCount: number;
}

export interface RoleFitCandidatesView {
  departmentName?: string;
  totalCandidates: number;
  // True when a company-wide pool was truncated; the client says so rather than implying completeness.
  isCapped: boolean;
  candidates: RoleFitCandidateView[];
}

export interface RequiredSkillItemView {
  skillId: number;
  skillName?: string;
  importance: EquipmentPriority;
}

export interface RequiredSkillItemInput {
  skillId: number;
  importance: EquipmentPriority;
}

export interface RequiredSkillSuggestionItemView {
  skillId: number;
  skillName: string;
  importance: EquipmentPriority;
}

export interface RequiredSkillsSuggestionView {
  skills: RequiredSkillSuggestionItemView[];
  rationale?: string;
  aiAvailable: boolean;
}

export interface SkillMappingSuggestionItemView {
  rawName: string;
  suggestedSkillId?: number;
  suggestedSkillName?: string;
  reason?: string;
}

export interface SkillMappingSuggestionView {
  mappings: SkillMappingSuggestionItemView[];
  aiAvailable: boolean;
}

export interface SkillGapRecommendationItemView {
  importance: EquipmentPriority;
  skillName: string;
  action: string;
  detail: string;
}

export interface SkillGapRecommendationView {
  summary: string;
  recommendations: SkillGapRecommendationItemView[];
  aiAvailable: boolean;
}

export async function getSkillRoleGaps(filter?: { departmentId?: number; businessRoleId?: number; period?: string }): Promise<Result<SkillRoleGapsView>> {
  const params = new URLSearchParams();
  if (filter?.departmentId !== undefined) params.append("departmentId", String(filter.departmentId));
  if (filter?.businessRoleId !== undefined) params.append("businessRoleId", String(filter.businessRoleId));
  // Owner decision 2026-09-29 #4 — the Q3 drill-down's period window ("yyyy-MM"); server-validated.
  if (filter?.period) params.append("period", filter.period);
  const qs = params.toString();
  const response = await request.get<Result<SkillRoleGapsView>>(`BusinessRole/GetSkillRoleGaps${qs ? `?${qs}` : ""}`);
  return response.data;
}

export async function getRoleFit(businessRoleId: number, employeeId: string): Promise<Result<StrengthAlignmentRowView>> {
  const response = await request.get<Result<StrengthAlignmentRowView>>(
    `BusinessRole/GetRoleFit/${businessRoleId}/${encodeURIComponent(employeeId)}`
  );
  return response.data;
}

export async function getRoleFitCandidates(businessRoleId: number): Promise<Result<RoleFitCandidatesView>> {
  const response = await request.get<Result<RoleFitCandidatesView>>(`BusinessRole/GetRoleFitCandidates/${businessRoleId}`);
  return response.data;
}

export async function suggestRequiredSkills(input: {
  name: string;
  category?: string;
  description?: string;
  coreResponsibilities?: string[];
}): Promise<Result<RequiredSkillsSuggestionView>> {
  const response = await request.post<Result<RequiredSkillsSuggestionView>>("BusinessRoleAi/SuggestRequiredSkills", input);
  return response.data;
}

export async function suggestSkillMappings(unresolvedNames: string[]): Promise<Result<SkillMappingSuggestionView>> {
  const response = await request.post<Result<SkillMappingSuggestionView>>("BusinessRoleAi/SuggestSkillMappings", { unresolvedNames });
  return response.data;
}

export async function recommendSkillGaps(employeeId: string): Promise<Result<SkillGapRecommendationView>> {
  const response = await request.post<Result<SkillGapRecommendationView>>(
    `BusinessRoleAi/RecommendSkillGaps/${encodeURIComponent(employeeId)}`
  );
  return response.data;
}
